"use client";

import { type ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1] as const;

type PublicHeroProps = {
  eyebrow?: string;
  eyebrowIcon?: ReactNode;
  title: ReactNode;
  subtitle?: string;
  className?: string;
  center?: boolean;
  children?: ReactNode;
};

export default function PublicHero({
  eyebrow,
  eyebrowIcon,
  title,
  subtitle,
  className,
  center = true,
  children,
}: PublicHeroProps) {
  return (
    <section className={cn("relative py-16 sm:py-24", className)} data-hero>
      <div
        className={cn(
          "relative z-10 mx-auto w-full max-w-6xl px-6",
          center && "text-center",
        )}
      >
        <div className={cn("mx-auto space-y-6", center && "max-w-2xl")}>
          {eyebrow && (
            <motion.span
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-y/40 bg-y/10 px-4 py-1.5 text-xs font-black uppercase tracking-[0.22em] text-y"
            >
              {eyebrowIcon && (
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  {eyebrowIcon}
                </span>
              )}
              {eyebrow}
            </motion.span>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
            className="display text-5xl leading-[0.95] tracking-tight text-foreground sm:text-6xl md:text-7xl"
          >
            {title}
          </motion.h1>

          {subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.12, ease: EASE }}
              className="mx-auto max-w-xl text-base font-medium leading-relaxed text-muted-foreground"
            >
              {subtitle}
            </motion.p>
          )}
        </div>

        {children && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18, ease: EASE }}
            className="mt-8"
          >
            {children}
          </motion.div>
        )}
      </div>
    </section>
  );
}
